import React from "react";
import { Box, Button, Divider, Group, Switch, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { usePromotionModuleEnabled } from "../helpers/appFeatures";
import {
  getPinnableSettingLinkIds,
  getSettingSidebarSections,
} from "../data/settingSidebarCatalog";
import {
  clearSettingPagePins,
  isSidebarPinned,
  setAllSettingPagesActive,
  setSidebarPin,
} from "../helpers/sidebarLinks";

const SettingSidebarAccessList = ({ links, sidebarLinks, setFieldValue }) => {
  const { t } = useTranslation();
  const promotionModuleEnabled = usePromotionModuleEnabled();
  const sections = getSettingSidebarSections(promotionModuleEnabled);
  const pinnableLinkIds = getPinnableSettingLinkIds(promotionModuleEnabled);

  const applySidebar = (next) => {
    setFieldValue("sidebarLinks", next.sidebarLinks);
    setFieldValue("links", next.links);
  };

  const onToggle = (linkId, pinned) => {
    applySidebar(
      setSidebarPin({
        links,
        sidebarLinks,
        linkId,
        pinned,
      })
    );
  };

  const onToggleAll = (pinned) => {
    if (pinned) {
      applySidebar(
        setAllSettingPagesActive({
          links,
          sidebarLinks,
          linkIds: pinnableLinkIds,
        })
      );
      return;
    }
    applySidebar(
      clearSettingPagePins({ links, sidebarLinks, linkIds: pinnableLinkIds })
    );
  };

  return (
    <>
      <Text size="sm" color="dimmed" mb="md">
        {t(
          "Turn a setting page on to show it and allow access. Turn it off to hide it from the sidebar and the Settings page."
        )}
      </Text>
      <Group mb="xl" spacing="xs">
        <Button size="xs" variant="light" onClick={() => onToggleAll(true)}>
          {t("Check all")}
        </Button>
        <Button size="xs" variant="default" onClick={() => onToggleAll(false)}>
          {t("Uncheck all")}
        </Button>
      </Group>
      {sections.map((section) => (
        <Box key={section.label} mb="xl">
          <Text size="sm" weight={500}>
            {t(section.label)}
          </Text>
          <Divider my="xs" />
          {section.items.map((item) => {
            const entry = (links ?? []).find((link) => link.link === item.linkId);
            const access = Number(entry?.accessRight ?? 0);
            const pinned =
              isSidebarPinned(sidebarLinks, item.linkId) || access > 0;
            return (
              <Group key={item.linkId} position="apart" mb="sm" noWrap>
                <Box sx={{ minWidth: 0 }}>
                  <Text size="sm">{t(item.label)}</Text>
                  <Text size="xs" color="dimmed">
                    {item.link}
                  </Text>
                </Box>
                <Switch
                  aria-label={`Show ${item.label} on sidebar`}
                  checked={pinned}
                  onChange={(event) =>
                    onToggle(item.linkId, event.currentTarget.checked)
                  }
                />
              </Group>
            );
          })}
        </Box>
      ))}
    </>
  );
};

export default SettingSidebarAccessList;
